import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useForm } from 'react-hook-form';
import './OrgAuth.css';
import { CFR_CATEGORIES } from "../../constants/cfrCategories";


function OrgSignup() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [shippingSame, setShippingSame] = useState(true);
  const [cfrCategories, setCfrCategories] = useState([]);
  const { register, handleSubmit, formState: { errors } } = useForm();

  function toggleCfr(value) {
    setCfrCategories(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  }

  async function onSubmit(data) {
    try {
      setError('');
      setLoading(true);

      const userCredential = await createUserWithEmailAndPassword(
        auth, data.email, data.password
      );
      const user = userCredential.user;
      const sameAsMailing = shippingSame;

      await setDoc(doc(db, 'organizations', user.uid), {
        uid: user.uid,
        orgName: data.orgName,
        contactName: data.contactName,
        contactTitle: data.contactTitle,
        email: data.email,
        phone: data.phone,
        address: data.address,
        city: data.city,
        state: data.state,
        zip: data.zip,
        website: data.website || '',
        ein: data.ein,
        orgType: data.orgType,
        populationsServed: data.populationsServed,
        missionStatement: data.missionStatement,
        cfrCategories,
        role: 'org',
        status: 'pending',
        approvedAt: null,
        createdAt: serverTimestamp(),
        lastVerified: null,
        trustFlags: [],
        deliveryConfirmationRate: null,
        community: 'springfield',
        shippingSameAsMailing: sameAsMailing,
        shippingAddress: sameAsMailing ? data.address : (data.shippingAddress || ''),
        shippingCity: sameAsMailing ? data.city : (data.shippingCity || ''),
        shippingState: sameAsMailing ? data.state : (data.shippingState || ''),
        shippingZip: sameAsMailing ? data.zip : (data.shippingZip || ''),
        shippingContactName: data.shippingContactName || data.contactName,
        shippingInstructions: data.shippingInstructions || ''
      });

      setSubmitted(true);
    } catch (err) {
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  }

  function getErrorMessage(code) {
    switch (code) {
      case 'auth/email-already-in-use': return 'An account with this email already exists.';
      case 'auth/weak-password': return 'Password must be at least 6 characters.';
      case 'auth/invalid-email': return 'Please enter a valid email address.';
      default: return 'Something went wrong. Please try again.';
    }
  }

  if (submitted) {
    return (
      <div className="auth-page org-auth-page">
        <div className="auth-card">
          <div className="auth-logo" onClick={() => navigate('/org')}>somebody.gives</div>
          <div className="submitted-state">
            <div className="submitted-icon">✓</div>
            <h1>Application received</h1>
            <p>Thank you for applying to join somebody.gives. We review every application personally and will be in touch within 2-3 business days.</p>
            <p className="submitted-note">Once approved, you will receive an email with instructions to log in and start listing your organization's needs.</p>
            <button onClick={() => navigate('/org')} className="btn-secondary btn-full">Back to home</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page org-auth-page">
      <div className="auth-card auth-card-wide">

        <div className="auth-logo" onClick={() => navigate('/org')}>somebody.gives</div>
        <h1>Apply to join</h1>
        <p className="auth-sub">Tell us about your organization. We review every application and will be in touch within 2-3 business days.</p>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit(onSubmit)} className="auth-form">

          <div className="form-section-label">Organization details</div>

          <div className="form-group">
            <label>Organization name</label>
            <input {...register('orgName', { required: 'Organization name is required' })} placeholder="Springfield Rescue Mission" />
            {errors.orgName && <span className="field-error">{errors.orgName.message}</span>}
          </div>

          <div className="form-group">
            <label>Organization type</label>
            <select {...register('orgType', { required: 'Please select a type' })}>
              <option value="">Select type...</option>
              <option value="homeless_shelter">Homeless shelter</option>
              <option value="youth_center">Youth center</option>
              <option value="diaper_bank">Diaper bank</option>
              <option value="domestic_violence">Domestic violence shelter</option>
              <option value="transitional_housing">Transitional housing</option>
              <option value="food_pantry">Food pantry</option>
              <option value="family_services">Family services</option>
              <option value="other">Other nonprofit</option>
            </select>
            {errors.orgType && <span className="field-error">{errors.orgType.message}</span>}
          </div>

          {/* ── Community Priority (CFR) ── */}
          <div className="form-group">
            <label>
              Community Priority (CFR)
              <span style={{ fontWeight: 400, color: '#888', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                — optional but strongly recommended
              </span>
            </label>
            <p style={{ fontSize: '0.82rem', color: '#666', marginBottom: '0.75rem', lineHeight: 1.5 }}>
              Which of Springfield's Community Focus Report priority areas does your organization's mission serve?
              This helps donors understand your community impact and connect your needs to local priorities.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {CFR_CATEGORIES.map(cat => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => toggleCfr(cat.value)}
                  style={{
                    padding: '0.35rem 0.875rem',
                    borderRadius: '20px',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    border: cfrCategories.includes(cat.value) ? 'none' : '1px solid #ddd',
                    background: cfrCategories.includes(cat.value) ? '#1e3a5f' : 'white',
                    color: cfrCategories.includes(cat.value) ? 'white' : '#555',
                  }}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            {cfrCategories.length === 0 && (
              <p style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '0.5rem' }}>
                No priorities selected — you can add these later from your profile.
              </p>
            )}
          </div>

          <div className="form-group">
            <label>Mission statement</label>
            <textarea {...register('missionStatement', { required: 'Mission statement is required' })} placeholder="Briefly describe your organization's mission and the people you serve..." rows={3} />
            {errors.missionStatement && <span className="field-error">{errors.missionStatement.message}</span>}
          </div>

          <div className="form-group">
            <label>Populations served</label>
            <input {...register('populationsServed', { required: 'Please describe who you serve' })} placeholder="e.g. Homeless adults, families in crisis, youth ages 12-18" />
            {errors.populationsServed && <span className="field-error">{errors.populationsServed.message}</span>}
          </div>

          <div className="form-group">
            <label>EIN (Tax ID)</label>
            <input {...register('ein', { required: 'EIN is required' })} placeholder="XX-XXXXXXX" />
            {errors.ein && <span className="field-error">{errors.ein.message}</span>}
          </div>

          <div className="form-group">
            <label>Website (optional)</label>
            <input {...register('website')} placeholder="https://yourorg.org" />
          </div>

          <div className="form-section-label">Mailing address</div>

          <div className="form-group">
            <label>Street address</label>
            <input {...register('address', { required: 'Address is required' })} placeholder="123 Main Street" />
            {errors.address && <span className="field-error">{errors.address.message}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input {...register('city', { required: 'City is required' })} placeholder="Springfield" defaultValue="Springfield" />
              {errors.city && <span className="field-error">{errors.city.message}</span>}
            </div>
            <div className="form-group">
              <label>State</label>
              <input {...register('state', { required: 'State is required' })} placeholder="MO" defaultValue="MO" maxLength={2} />
              {errors.state && <span className="field-error">{errors.state.message}</span>}
            </div>
            <div className="form-group">
              <label>ZIP</label>
              <input {...register('zip', { required: 'ZIP is required' })} placeholder="65801" maxLength={5} />
              {errors.zip && <span className="field-error">{errors.zip.message}</span>}
            </div>
          </div>

          <div className="form-section-label">Shipping address</div>
          <p className="form-section-note">Where should donated supplies be delivered? This is the physical address Amazon will ship to. Must be a street address, no PO Boxes.</p>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input type="checkbox" defaultChecked={true} onChange={e => setShippingSame(e.target.checked)} />
              <span>Shipping address is the same as mailing address</span>
            </label>
          </div>

          {!shippingSame && (
            <>
              <div className="form-group">
                <label>Shipping street address</label>
                <input {...register('shippingAddress', { required: !shippingSame ? 'Shipping address is required' : false })} placeholder="123 Main Street" />
                {errors.shippingAddress && <span className="field-error">{errors.shippingAddress.message}</span>}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input {...register('shippingCity', { required: !shippingSame ? 'City is required' : false })} placeholder="Springfield" defaultValue="Springfield" />
                  {errors.shippingCity && <span className="field-error">{errors.shippingCity.message}</span>}
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input {...register('shippingState', { required: !shippingSame ? 'State is required' : false })} placeholder="MO" defaultValue="MO" maxLength={2} />
                  {errors.shippingState && <span className="field-error">{errors.shippingState.message}</span>}
                </div>
                <div className="form-group">
                  <label>ZIP</label>
                  <input {...register('shippingZip', { required: !shippingSame ? 'ZIP is required' : false })} placeholder="65801" maxLength={5} />
                  {errors.shippingZip && <span className="field-error">{errors.shippingZip.message}</span>}
                </div>
              </div>
              <div className="form-group">
                <label>Receiving contact name</label>
                <input {...register('shippingContactName')} placeholder="Name of person who receives deliveries" />
              </div>
              <div className="form-group">
                <label>Delivery instructions (optional)</label>
                <input {...register('shippingInstructions')} placeholder="e.g. Ring bell at back door, leave with front desk" />
              </div>
            </>
          )}

          <div className="form-section-label">Primary contact</div>

          <div className="form-row">
            <div className="form-group">
              <label>Contact name</label>
              <input {...register('contactName', { required: 'Contact name is required' })} placeholder="Jane Smith" />
              {errors.contactName && <span className="field-error">{errors.contactName.message}</span>}
            </div>
            <div className="form-group">
              <label>Title</label>
              <input {...register('contactTitle')} placeholder="Executive Director" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email address</label>
              <input {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+$/i, message: 'Please enter a valid email' } })} placeholder="contact@yourorg.org" type="email" />
              {errors.email && <span className="field-error">{errors.email.message}</span>}
            </div>
            <div className="form-group">
              <label>Phone number</label>
              <input {...register('phone', { required: 'Phone is required' })} placeholder="(417) 555-0100" type="tel" />
              {errors.phone && <span className="field-error">{errors.phone.message}</span>}
            </div>
          </div>

          <div className="form-section-label">Account security</div>

          <div className="form-group">
            <label>Password</label>
            <input {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Password must be at least 6 characters' } })} placeholder="At least 6 characters" type="password" />
            {errors.password && <span className="field-error">{errors.password.message}</span>}
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input type="checkbox" {...register('terms', { required: 'You must agree to continue' })} />
              <span>I agree to the <a href="/terms" target="_blank">terms of service</a> and understand that my application will be reviewed before activation.</span>
            </label>
            {errors.terms && <span className="field-error">{errors.terms.message}</span>}
          </div>

          <button type="submit" className="btn-primary btn-full" disabled={loading}>
            {loading ? 'Submitting application...' : 'Submit application →'}
          </button>

        </form>

        <div className="auth-footer">
          Already approved? <span onClick={() => navigate('/org/login')}>Sign in</span>
        </div>

      </div>
    </div>
  );
}

export default OrgSignup;
